import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const LINES = ["ANCHOR", "POINT", "INDUSTRIES"];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.5 + i * 0.12 },
  }),
};

export const Hero = ({ onQuote }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen w-full overflow-hidden bg-background" data-testid="hero">
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src="https://images.pexels.com/photos/11347815/pexels-photo-11347815.jpeg"
          alt="Industrial pipe and valve facility"
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/50 to-background" />
      </motion.div>
      <div className="absolute inset-0 z-[1] blueprint-grid opacity-60" />

      {/* Top meta bar */}
      <motion.div
        style={{ opacity }}
        className="absolute top-[92px] left-0 right-0 z-10 max-w-[1500px] mx-auto px-5 md:px-10 flex justify-between font-mono-tech text-[10px] tracking-[0.25em] uppercase text-steel"
      >
        <span>PVF & Industrial Supply</span>
        <span className="hidden md:block">Est. Procurement Partner</span>
        <span>Houston · Gulf Coast · Nationwide</span>
      </motion.div>

      {/* Main heading */}
      <div className="relative z-10 max-w-[1500px] mx-auto px-5 md:px-10 min-h-screen flex flex-col justify-center pt-24 pb-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="font-mono-tech text-[11px] md:text-xs tracking-[0.3em] uppercase text-primary mb-8 flex items-center gap-3"
        >
          <span className="inline-block w-10 h-px bg-accent" />
          Pipe · Valves · Fittings · Materials
        </motion.div>

        <h1 className="font-display font-black uppercase leading-[0.85] tracking-tighter text-foreground">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="show"
                className={`block text-[17vw] md:text-[13vw] lg:text-[11.5rem] ${
                  i === 2 ? "text-primary" : "text-foreground"
                }`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <p className="max-w-md text-base md:text-lg text-steel leading-relaxed">
            Your anchor point for contractor procurement. We source pipe, valves,
            fittings, gaskets, fasteners and jobsite materials — accurately, and on schedule.
          </p>
          <button
            onClick={onQuote}
            data-testid="hero-quote-btn"
            className="group inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 font-mono-tech text-xs tracking-[0.2em] uppercase hover:bg-accent hover:text-accent-foreground transition-colors duration-300 self-start"
          >
            Request a Quote
            <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </button>
        </motion.div>
      </div>

      <motion.div
        style={{ opacity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-steel"
      >
        <span className="font-mono-tech text-[9px] tracking-[0.3em] uppercase">Scroll</span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.div>
    </section>
  );
};
